import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHistory, FaInfoCircle } from 'react-icons/fa';
import { KavachSwitch } from '../utils/kavachTypes';
import { getSwitchHealth } from '../utils/kavachConstants';

interface KavachSwitchTableProps {
  switches: KavachSwitch[];
  onSwitchClick: (sw: KavachSwitch) => void;
}

const KavachSwitchTable: React.FC<KavachSwitchTableProps> = ({ switches, onSwitchClick }) => {
  const navigate = useNavigate();

  const getHealthLabel = (health: 'healthy' | 'partial' | 'critical') => {
    switch (health) {
      case 'healthy':
        return 'All Up';
      case 'partial':
        return 'Partial';
      case 'critical':
        return 'Critical';
    }
  };

  const getOverallStatus = (sw: KavachSwitch) => {
    const ports = Object.values(sw.ports);
    const upCount = ports.filter((p) => p.status === 'UP').length;
    return `${upCount}/4 Up`;
  };

  const handleViewDetails = (e: React.MouseEvent, sw: KavachSwitch) => {
    e.stopPropagation();
    navigate(`/kavach-switch-detail?deviceId=${sw.id}&deviceName=${sw.name}`);
  };

  const handleViewHistory = (e: React.MouseEvent, sw: KavachSwitch) => {
    e.stopPropagation();
    navigate(`/kavach-switch-history/${sw.id}`);
  };

  return (
    <div className="table-container">
      <div className="table-header">
        <div className="table-title">
          Network Switches Status
          {/* <span>(Click any switch for quick view)</span> */}
        </div>
        <div className="table-count">Showing {switches.length} switches</div>
      </div>
      <div className="table-scroll">
        <table className="switches-table">
          <thead>
            <tr>
              <th>Last Activity</th>
              <th>Device Name</th>
              <th>Device Type</th>
              <th>Station</th>
              <th>Port 1</th>
              <th>Port 2</th>
              <th>Port 3</th>
              <th>Port 4</th>
              <th>Overall Status</th>
              <th>Health</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {switches.map((sw) => {
              const health = getSwitchHealth(sw);
              return (
                <tr key={sw.id} onClick={() => onSwitchClick(sw)}>
                  <td className="last-activity">{sw.lastActivity}</td>
                  <td className="switch-name-cell">{sw.name}</td>
                  <td className="device-type-cell">{sw.deviceType || 'Media Converter'}</td>
                  <td>
                    <span className="station-badge">{sw.station}</span>
                  </td>
                  <td>
                    <span className={`port-status ${sw.ports.port1.status.toLowerCase()}`}>
                      {sw.ports.port1.status === 'UP' ? 'U' : 'D'}
                    </span>
                  </td>
                  <td>
                    <span className={`port-status ${sw.ports.port2.status.toLowerCase()}`}>
                      {sw.ports.port2.status === 'UP' ? 'U' : 'D'}
                    </span>
                  </td>
                  <td>
                    <span className={`port-status ${sw.ports.port3.status.toLowerCase()}`}>
                      {sw.ports.port3.status === 'UP' ? 'U' : 'D'}
                    </span>
                  </td>
                  <td>
                    <span className={`port-status ${sw.ports.port4.status.toLowerCase()}`}>
                      {sw.ports.port4.status === 'UP' ? 'U' : 'D'}
                    </span>
                  </td>
                  <td>{getOverallStatus(sw)}</td>
                  <td>
                    <span className={`health-badge ${health}`}>{getHealthLabel(health)}</span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="action-btn detail"
                      onClick={(e) => handleViewDetails(e, sw)}
                      title="View Details"
                    >
                      <FaInfoCircle />
                    </button>
                    <button
                      className="action-btn history"
                      onClick={(e) => handleViewHistory(e, sw)}
                      title="View History"
                    >
                      <FaHistory />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KavachSwitchTable;
